import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AiChatDecisionAnswer } from '@amitable/shared/ai-chat-decisions'

export function isUnanswered(answer: AiChatDecisionAnswer | undefined) {
  return !answer?.selectedOptionIds.length && !answer?.customText?.trim()
}

type StepsInput = {
  answers: Array<AiChatDecisionAnswer>
  locked: boolean | undefined
  onError: (message: string | undefined) => void
}

export function useAiChatDecisionSteps({
  answers,
  locked,
  onError,
}: StepsInput) {
  const { t } = useTranslation('aiChat')
  const [requested, setRequested] = useState(0)
  const headingRef = useRef<HTMLLegendElement>(null)
  const navigated = useRef(false)
  const total = answers.length
  const step = Math.min(requested, total - 1)
  const isLast = step === total - 1

  useEffect(() => {
    if (!navigated.current) return
    navigated.current = false
    headingRef.current?.focus()
  }, [step])

  const goTo = (target: number) => {
    navigated.current = true
    onError(undefined)
    setRequested(target)
  }
  const back = () => {
    if (!locked && step > 0) goTo(step - 1)
  }
  const next = () => {
    if (locked || isLast) return
    if (isUnanswered(answers[step])) {
      onError(t('decisionStepRequired'))

      return
    }
    goTo(step + 1)
  }

  return { step, total, isLast, headingRef, back, next }
}
